import { Link, useLoaderData, useNavigate } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  DollarSign,
  Percent,
  ShieldAlert,
  Target,
  TrendingUp,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatFY } from "@/lib/fiscal";
import { cn } from "@/lib/utils";
import {
  FY_OPTIONS,
  type FYQuarter,
  formatCurrency,
  formatNumber,
  formatPercent,
  getCurrentFiscalQuarter,
  getFiscalYearDates,
  type MetricType,
  type Pipeline,
  resolvePipelineIds,
} from "./shared";

interface OwnerMetrics {
  hubspotOwnerId: string;
  ownerName: string | null;
  opportunities: number;
  closedDeals: number;
  errorCount: number;
  errorRate: number;
  grossSales: number;
  netSales: number;
  grossProfitMargin: number;
  closeRate: number;
}

interface MetricsResponse {
  owners: OwnerMetrics[];
  totals: {
    opportunities: number;
    closedDeals: number;
    errorCount: number;
    errorRate: number;
    grossSales: number;
    netSales: number;
    grossProfitMargin: number;
    closeRate: number;
  };
}

const { fy: currentFY, quarter: currentQuarter } = getCurrentFiscalQuarter();
const { start: DEFAULT_START, end: DEFAULT_END } = getFiscalYearDates(currentFY, currentQuarter);

type SortKey =
  | "ownerName"
  | "opportunities"
  | "closedDeals"
  | "errorRate"
  | "grossSales"
  | "netSales"
  | "grossProfitMargin"
  | "closeRate";

export function DealMetricsPage() {
  const { pipelines } = useLoaderData({ from: "/_authenticated/deal-metrics" }) as {
    pipelines: Pipeline[];
  };

  const [startDate, setStartDate] = useState(DEFAULT_START);
  const [endDate, setEndDate] = useState(DEFAULT_END);
  const [selectedFY, setSelectedFY] = useState(currentFY);
  const [selectedQuarter, setSelectedQuarter] = useState<FYQuarter>(currentQuarter);
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("grossSales");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function applyPeriod(fy: number, quarter: FYQuarter) {
    setSelectedFY(fy);
    setSelectedQuarter(quarter);
    const { start, end } = getFiscalYearDates(fy, quarter);
    setStartDate(start);
    setEndDate(end);
  }

  const navigate = useNavigate();

  // Resolve pipeline IDs from labels
  const { salesPipelineId, prodPipelineId, excludeOppStageIds, excludeClosedStageIds } = useMemo(
    () => resolvePipelineIds(pipelines),
    [pipelines],
  );

  const fetchMetrics = useCallback(async () => {
    if (!prodPipelineId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ startDate, endDate, prodPipelineId });
      if (salesPipelineId) params.set("salesPipelineId", salesPipelineId);
      if (excludeOppStageIds.length > 0)
        params.set("excludeOppStageIds", excludeOppStageIds.join(","));
      if (excludeClosedStageIds.length > 0)
        params.set("excludeClosedStageIds", excludeClosedStageIds.join(","));

      const res = await fetch(`/api/deal-metrics?${params}`);
      if (res.ok) {
        setData(await res.json());
      }
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    salesPipelineId,
    prodPipelineId,
    excludeOppStageIds,
    excludeClosedStageIds,
  ]);

  useEffect(() => {
    const timer = setTimeout(fetchMetrics, 300);
    return () => clearTimeout(timer);
  }, [fetchMetrics]);

  function navigateToDetail(metricType: MetricType, hubspotOwnerId: string | null) {
    if (!prodPipelineId) return;
    const search: Record<string, string> = {
      metric: metricType,
      startDate,
      endDate,
      prodPipelineId,
    };
    if (salesPipelineId) search.salesPipelineId = salesPipelineId;
    if (excludeOppStageIds.length > 0) search.excludeOppStageIds = excludeOppStageIds.join(",");
    if (excludeClosedStageIds.length > 0)
      search.excludeClosedStageIds = excludeClosedStageIds.join(",");
    if (hubspotOwnerId) search.hubspotOwnerId = hubspotOwnerId;
    navigate({ to: "/deal-metrics/detail", search: search as any });
  }

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "ownerName" ? "asc" : "desc");
    }
  }

  const sortedOwners = useMemo(() => {
    if (!data) return [];
    return [...data.owners].sort((a, b) => {
      let cmp: number;
      if (sortKey === "ownerName") {
        cmp = (a.ownerName ?? "").localeCompare(b.ownerName ?? "");
      } else {
        cmp = a[sortKey] - b[sortKey];
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  // Clickable metric cell — navigates to detail page
  function MetricCell({
    value,
    metricType,
    hubspotOwnerId,
    className,
  }: {
    value: string;
    metricType: MetricType;
    hubspotOwnerId: string | null;
    className?: string;
  }) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          navigateToDetail(metricType, hubspotOwnerId);
        }}
        className={cn(
          "text-right tabular-nums hover:text-primary hover:underline underline-offset-2 cursor-pointer",
          className,
        )}
      >
        {value}
      </button>
    );
  }

  const summaryCards = data
    ? [
        {
          label: "Opportunities",
          value: formatNumber(data.totals.opportunities),
          icon: Target,
          metric: "opportunities" as MetricType,
        },
        {
          label: "Closed Deals",
          value: formatNumber(data.totals.closedDeals),
          icon: TrendingUp,
          metric: "closedDeals" as MetricType,
        },
        {
          label: "Error Rate",
          value: formatPercent(data.totals.errorRate),
          icon: ShieldAlert,
          metric: "errors" as MetricType,
        },
        {
          label: "Gross Sales",
          value: formatCurrency(data.totals.grossSales),
          icon: DollarSign,
          metric: "grossSales" as MetricType,
        },
        {
          label: "Net Sales",
          value: formatCurrency(data.totals.netSales),
          icon: DollarSign,
          metric: "netSales" as MetricType,
        },
        {
          label: "GPM",
          value: formatPercent(data.totals.grossProfitMargin),
          icon: Percent,
          metric: null,
        },
        {
          label: "Close Rate",
          value: formatPercent(data.totals.closeRate),
          icon: Percent,
          metric: "closeRate" as MetricType,
        },
      ]
    : null;

  if (!prodPipelineId) {
    return (
      <div className="space-y-6">
        <PageHeader title="Deal Metrics" description="Per-consultant performance reporting" />
        <EmptyState
          icon={BarChart3}
          heading="No Production Pipeline Found"
          description="Could not find a pipeline with 'Production' in its name. Ensure your HubSpot pipelines are configured correctly."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Deal Metrics"
        description="Per-consultant performance reporting"
        actions={
          <div className="flex flex-col items-end gap-2">
            {/* FY + Quarter toggles */}
            <div className="flex items-center gap-2">
              <Select
                value={String(selectedFY)}
                onValueChange={(v) => applyPeriod(Number(v), selectedQuarter)}
              >
                <SelectTrigger className="w-[120px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FY_OPTIONS.map((fy) => (
                    <SelectItem key={fy} value={String(fy)}>
                      {formatFY(fy)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(["Q1", "Q2", "Q3", "Q4", "FY"] as FYQuarter[]).map((q) => (
                <Button
                  key={q}
                  variant={selectedQuarter === q ? "default" : "outline"}
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={() => applyPeriod(selectedFY, q)}
                >
                  {q === "FY" ? "Full Year" : q}
                </Button>
              ))}
            </div>
            {/* Date pickers */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">From</span>
              <Input
                type="date"
                aria-label="Start date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-[140px] h-8 text-xs"
              />
              <span className="text-xs text-muted-foreground">To</span>
              <Input
                type="date"
                aria-label="End date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-[140px] h-8 text-xs"
              />
            </div>
          </div>
        }
      />

      {/* Summary Cards — clickable for drill-down */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7 animate-stagger">
        {loading || !summaryCards
          ? Array.from({ length: 7 }).map((_, i) => (
              <Card key={`skel-${i}`} className="py-4">
                <CardContent className="flex flex-col gap-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-7 w-24" />
                </CardContent>
              </Card>
            ))
          : summaryCards.map((card) => (
              <Card
                key={card.label}
                className={cn(
                  "py-4 group transition-shadow",
                  card.metric && "hover:shadow-md cursor-pointer",
                )}
                onClick={card.metric ? () => navigateToDetail(card.metric, null) : undefined}
              >
                <CardContent className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    <card.icon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {card.label}
                    </span>
                  </div>
                  <span
                    className={cn(
                      "text-xl font-bold tabular-nums tracking-tight transition-colors",
                      card.metric && "group-hover:text-primary",
                    )}
                  >
                    {card.value}
                  </span>
                </CardContent>
              </Card>
            ))}
      </div>

      {/* Per-Owner Table */}
      <div className={cn("rounded-md border", loading && "opacity-60")}>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              {(
                [
                  { key: "ownerName", label: "Consultant" },
                  { key: "opportunities", label: "Opportunities" },
                  { key: "closedDeals", label: "Closed Deals" },
                  { key: "errorRate", label: "Error Rate" },
                  { key: "grossSales", label: "Gross Sales" },
                  { key: "netSales", label: "Net Sales" },
                ] as { key: SortKey; label: string }[]
              ).map((col) => (
                <TableHead key={col.key} className={col.key !== "ownerName" ? "text-right" : ""}>
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    className={cn(
                      "flex items-center text-xs uppercase tracking-wider hover:text-foreground",
                      col.key !== "ownerName" && "ml-auto",
                    )}
                  >
                    {col.label}
                    {sortKey === col.key ? (
                      sortDir === "asc" ? (
                        <ArrowUp className="ml-1 h-3 w-3" />
                      ) : (
                        <ArrowDown className="ml-1 h-3 w-3" />
                      )
                    ) : (
                      <ArrowUpDown className="ml-1 h-3 w-3 opacity-30" />
                    )}
                  </button>
                </TableHead>
              ))}
              <TableHead className="text-right">
                <span className="flex items-center ml-auto text-xs uppercase tracking-wider">
                  Net Sales %
                </span>
              </TableHead>
              {(
                [
                  { key: "grossProfitMargin", label: "GPM" },
                  { key: "closeRate", label: "Close Rate" },
                ] as { key: SortKey; label: string }[]
              ).map((col) => (
                <TableHead key={col.key} className="text-right">
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    className="flex items-center text-xs uppercase tracking-wider hover:text-foreground ml-auto"
                  >
                    {col.label}
                    {sortKey === col.key ? (
                      sortDir === "asc" ? (
                        <ArrowUp className="ml-1 h-3 w-3" />
                      ) : (
                        <ArrowDown className="ml-1 h-3 w-3" />
                      )
                    ) : (
                      <ArrowUpDown className="ml-1 h-3 w-3 opacity-30" />
                    )}
                  </button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`row-skel-${i}`}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <TableCell key={`cell-skel-${j}`}>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : sortedOwners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                  No deal data found for this date range.
                </TableCell>
              </TableRow>
            ) : (
              <>
                {sortedOwners.map((owner) => {
                  return (
                    <TableRow key={owner.hubspotOwnerId} className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        {owner.ownerName ? (
                          <Link
                            to="/owner-metrics/$ownerId"
                            params={{ ownerId: owner.hubspotOwnerId }}
                            className="hover:text-primary hover:underline underline-offset-2"
                          >
                            {owner.ownerName}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground italic">Unknown</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <MetricCell
                          value={formatNumber(owner.opportunities)}
                          metricType="opportunities"
                          hubspotOwnerId={owner.hubspotOwnerId}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <MetricCell
                          value={formatNumber(owner.closedDeals)}
                          metricType="closedDeals"
                          hubspotOwnerId={owner.hubspotOwnerId}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <MetricCell
                          value={formatPercent(owner.errorRate)}
                          metricType="errors"
                          hubspotOwnerId={owner.hubspotOwnerId}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <MetricCell
                          value={formatCurrency(owner.grossSales)}
                          metricType="grossSales"
                          hubspotOwnerId={owner.hubspotOwnerId}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <MetricCell
                          value={formatCurrency(owner.netSales)}
                          metricType="netSales"
                          hubspotOwnerId={owner.hubspotOwnerId}
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPercent(
                          owner.grossSales > 0 ? owner.netSales / owner.grossSales : 0,
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPercent(owner.grossProfitMargin)}
                      </TableCell>
                      <TableCell className="text-right">
                        <MetricCell
                          value={formatPercent(owner.closeRate)}
                          metricType="closeRate"
                          hubspotOwnerId={owner.hubspotOwnerId}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Totals row */}
                {data && sortedOwners.length > 1 && (
                  <TableRow className="bg-muted/20 font-semibold border-t-2">
                    <TableCell>Totals</TableCell>
                    <TableCell className="text-right">
                      <MetricCell
                        value={formatNumber(data.totals.opportunities)}
                        metricType="opportunities"
                        hubspotOwnerId={null}
                        className="font-semibold"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <MetricCell
                        value={formatNumber(data.totals.closedDeals)}
                        metricType="closedDeals"
                        hubspotOwnerId={null}
                        className="font-semibold"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <MetricCell
                        value={formatPercent(data.totals.errorRate)}
                        metricType="errors"
                        hubspotOwnerId={null}
                        className="font-semibold"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <MetricCell
                        value={formatCurrency(data.totals.grossSales)}
                        metricType="grossSales"
                        hubspotOwnerId={null}
                        className="font-semibold"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <MetricCell
                        value={formatCurrency(data.totals.netSales)}
                        metricType="netSales"
                        hubspotOwnerId={null}
                        className="font-semibold"
                      />
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {formatPercent(
                        data.totals.grossSales > 0
                          ? data.totals.netSales / data.totals.grossSales
                          : 0,
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {formatPercent(data.totals.grossProfitMargin)}
                    </TableCell>
                    <TableCell className="text-right">
                      <MetricCell
                        value={formatPercent(data.totals.closeRate)}
                        metricType="closeRate"
                        hubspotOwnerId={null}
                        className="font-semibold"
                      />
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
